/**
 * THE ONE RULE for a caller-supplied "where to go after sign-in" value.
 *
 * S-059 (2026-09-12) put a guard on the sign-in callbacks: a single leading
 * slash, not two, no backslash, no CR or LF. S-160 (2026-09-18) found it
 * bypassed with a TAB. The WHATWG URL parser strips tab and newline characters
 * before it parses, so `/\t/evil.example` passed every one of those checks and
 * `new URL('/\t/evil.example', 'https://robutler.ai')` is `https://evil.example/`.
 * Every callback finishes with exactly that `new URL(value, base)`, so a link
 * that began on our own domain landed a person who had just signed in on a
 * page somebody else owns. The tech-week launch send funnels 60,000 people
 * through `/login?returnTo=`, which is where it would have been used.
 *
 * Two layers, because either alone has been wrong once:
 *   1. refuse the characters a parser drops or reinterprets: every C0
 *      control character and DEL, and the backslash, which WHATWG treats as
 *      a slash in special schemes;
 *   2. then ask the parser itself: resolve the value against a fixed origin
 *      and require the result to still be on that origin.
 *
 * Plain module on purpose, no server-only imports: the login page's own copy
 * of this rule used to be written out inline because the server helper lives
 * in a module the client may not import, and three copies of a security rule
 * are three places for it to drift. They all call this now.
 */

const MAX_LEN = 512;
const PROBE_ORIGIN = 'https://return-path.invalid';

/** True when `value` is a same-origin path that is safe to redirect to. */
export function isSafeReturnPath(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (!value || value.length > MAX_LEN) return false;
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  if (/[\u0000-\u001f\u007f\\]/.test(value)) return false;
  try {
    return new URL(value, PROBE_ORIGIN).origin === PROBE_ORIGIN;
  } catch {
    return false;
  }
}
